import React from "react";
import * as THREE from "three";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls";
import { WireframedBox } from "../lib/wireframedBox";
import { TinyColor } from "@ctrl/tinycolor";
import "./scene.css";

let camera, scene, renderer, controls, skinTexture;
let canvas, drawingCanvas, drawingContext;
let container;
let color;
let layerCanvas = null;
let bodyGroup = new THREE.Group();
let overlayGroup = new THREE.Group();
let bodyWireframeGroup = [];
let model = "steve";
let penMode = "pen";
let showBody = true;
let showOverlay = true;
let isMirrored = false;
let onColorPick = () => { };
let onSaveHistory = () => { };
var i = 0;

class Vector4 {
  constructor(array) {
    this.x = array[0];
    this.y = array[1];
    this.w = array[2];
    this.h = array[3];
  }

  intersected(x, y) {
    return (
      x >= this.x && x < this.x + this.w && y >= this.y && y < this.y + this.h
    );
  }

  localX(x) {
    return x - this.x;
  }

  localY(y) {
    return y - this.y;
  }
}

class FaceMap {
  constructor([face1, face2, size]) {
    this.face1 = new Vector4(face1);
    this.face2 = new Vector4(face2);
    this.size = size;
  }

  intersected(x, y) {
    return this.face1.intersected(x, y) || this.face2.intersected(x, y);
  }

  findReflection(x, y) {
    if (this.face1.intersected(x, y)) {
      return {
        x: this.face2.x + this.face2.w - this.face1.localX(x) - 1,
        y: this.face2.y + this.face1.localY(y),
      };
    } else if (this.face2.intersected(x, y)) {
      return {
        x: this.face1.x + this.face1.w - this.face2.localX(x) - 1,
        y: this.face1.y + this.face2.localY(y),
      };
    }
  }
}

const maps = [
  // 4x arms
  [[40, 20, 4, 12], [40, 52, 4, 12], 4],
  [[44, 20, 4, 12], [36, 52, 4, 12], 4],
  [[48, 20, 4, 12], [32, 52, 4, 12], 4],
  [[52, 20, 4, 12], [44, 52, 4, 12], 4],
  [[44, 16, 4, 4], [36, 48, 4, 4], 4],
  [[48, 16, 4, 4], [40, 48, 4, 4], 4],

  // 4x overlays
  [[52, 52, 4, 12], [44, 36, 4, 12], 4],
  [[56, 52, 4, 12], [40, 36, 4, 12], 4],
  [[60, 52, 4, 12], [52, 36, 4, 12], 4],
  [[48, 52, 4, 12], [48, 36, 4, 12], 4],
  [[44, 32, 4, 4], [52, 48, 4, 4], 4],
  [[48, 32, 4, 4], [56, 48, 4, 4], 4],

  // 4x legs
  [
    [4, 36, 4, 12],
    [4, 52, 4, 12],
  ],
  [
    [8, 36, 4, 12],
    [0, 52, 4, 12],
  ],
  [
    [12, 36, 4, 12],
    [12, 52, 4, 12],
  ],
  [
    [0, 36, 4, 12],
    [8, 52, 4, 12],
  ],
  [
    [4, 32, 4, 4],
    [4, 48, 4, 4],
  ],
  [
    [8, 32, 4, 4],
    [8, 48, 4, 4],
  ],
  [
    [4, 20, 4, 12],
    [20, 52, 4, 12],
  ],
  [
    [0, 20, 4, 12],
    [24, 52, 4, 12],
  ],
  [
    [12, 20, 4, 12],
    [28, 52, 4, 12],
  ],
  [
    [8, 20, 4, 12],
    [16, 52, 4, 12],
  ],
  [
    [4, 16, 4, 4],
    [20, 48, 4, 4],
  ],
  [
    [8, 16, 4, 4],
    [24, 48, 4, 4],
  ],

  // 3x arms
  [[36, 52, 3, 12], [44, 20, 3, 12], 3],
  [[39, 52, 4, 12], [40, 20, 4, 12], 3],
  [[43, 52, 3, 12], [51, 20, 3, 12], 3],
  [[32, 52, 4, 12], [47, 20, 4, 12], 3],
  [[44, 16, 3, 4], [36, 48, 3, 4], 3],
  [[47, 16, 3, 4], [39, 48, 3, 4], 3],

  // 3x overlays
  [[52, 52, 3, 12], [44, 36, 3, 12], 3],
  [[55, 52, 4, 12], [40, 36, 4, 12], 3],
  [[59, 52, 3, 12], [51, 36, 3, 12], 3],
  [[48, 52, 4, 12], [47, 36, 4, 12], 3],
  [[44, 32, 3, 4], [52, 48, 3, 4], 3],
  [[47, 32, 3, 4], [55, 48, 3, 4], 3],
];

const convertedMaps = maps.map((item) => new FaceMap(item));

let steve = [];
let alex = [];

const raycaster = new THREE.Raycaster();
const mouse = new THREE.Vector2();
const onClickPosition = new THREE.Vector2();

function init() {
  camera = new THREE.PerspectiveCamera(80, 1, 0.1, 1000);
  camera.position.set(0, 0, 40);
  if (window.innerWidth < 768) {
    camera.position.x /= 1.5;
    camera.position.y /= 1.5;
    camera.position.z /= 1.5;
  }


  scene = new THREE.Scene();

  renderer = new THREE.WebGLRenderer({
    canvas: document.querySelector("canvas"),
    antialias: true,
  });
  renderer.setPixelRatio(window.devicePixelRatio);
  renderer.setClearColor(0xffffff, 0);
  //renderer.setSize( window.innerWidth / 2, window.innerHeight);

  controls = new OrbitControls(camera, renderer.domElement);
  controls.target.set(0, 0, 0);
  controls.mouseButtons = {
    LEFT: THREE.MOUSE.ROTATE,
    MIDDLE: THREE.MOUSE.ROTATE,
    MOUSEWHEEL: THREE.MOUSE.DOLLY,
    RIGHT: THREE.MOUSE.ROTATE,
  };
  controls.minDistance = 20;
  controls.maxDistance = 50;
  controls.update();

  container = document.getElementById("container");
  container.appendChild(renderer.domElement);
  container.addEventListener("pointermove", onMouseMove);
  container.addEventListener("pointerdown", onMouseDown);
  container.addEventListener("pointerup", onMouseUp);
  // window.addEventListener( 'resize', onWindowResize );
}

function onMouseDown(evt) {
  evt.preventDefault();
  if (evt.button === 0) {
    const array = getMousePosition(container, evt.clientX, evt.clientY);
    onClickPosition.fromArray(array);
    const intersects = getIntersects(onClickPosition, scene.children);
    const intersect = intersects.find((item) => item.object.visible && item.uv);

    if (intersect) {
      const uv = intersect.uv;
      const index = intersect.face.materialIndex;
      const map = intersect.object.material[index].map;
      map.transformUv(uv);

      if (penMode === "picker") {
        const colors = intersects
          .filter((item) => item.object.visible && item.uv)
          .map((item) =>
            item.object.material[item.face.materialIndex].map.transformUv(
              item.uv
            )
          )
          .map((uv) => canvas.colorPick(uv.x, uv.y))
          .filter((rgba) => rgba[3] > 0);
        const picked = colors.find((item) => item[3] === 255);
        if (picked) onColorPick(picked);
      } else if (penMode === "bucket") {
        canvas.fillPixels(
          map.rotation ? map.offset.x - map.repeat.x : map.offset.x,
          map.rotation ? map.offset.y - map.repeat.y : map.offset.y,
          map.repeat.x,
          map.repeat.y
        );
      } else {
        if (penMode === "eraser") {
          canvas.erasePixel(uv.x, uv.y, intersect.object.isOverlay);
        } else {
          canvas.drawPixel(uv.x, uv.y);
        }
        controls.enabled = false;
      }
    } else {
      controls.enabled = true;
    }
  }
}

function onMouseUp(evt) {
  evt.preventDefault();
  if (!controls.enabled) {
    onSaveHistory();
  }
  controls.enabled = true;
}

function onMouseMove(evt) {
  const array = getMousePosition(container, evt.clientX, evt.clientY);
  onClickPosition.fromArray(array);
  const intersects = getIntersects(onClickPosition, scene.children);
  const intersect = intersects.find((item) => item.object.visible && item.uv);

  if (controls.enabled === false) {
    if (intersect) {
      const uv = intersect.uv;
      const index = intersect.face.materialIndex;
      intersect.object.material[index].map.transformUv(uv);
      if (penMode === "eraser") {
        canvas.erasePixel(uv.x, uv.y, intersect.object.isOverlay);
      } else {
        canvas.drawPixel(uv.x, uv.y);
      }
    }
  }
}

function getMousePosition(dom, x, y) {
  const rect = dom.getBoundingClientRect();
  let rendererXY = new THREE.Vector2();
  renderer.getSize(rendererXY);
  return [
    (x - rect.left) / rendererXY.width,
    (y - rect.top) / rendererXY.height,
  ];
}

function getIntersects(point, objects) {
  mouse.set(point.x * 2 - 1, -(point.y * 2) + 1);
  raycaster.setFromCamera(mouse, camera);
  return raycaster.intersectObjects(objects);
}

function CanvasTexture(parentTexture) {
  this._canvas = drawingCanvas || null;
  this._context2D = drawingContext || null;
  this._image = drawingContext.createImageData(
    drawingCanvas.width,
    drawingCanvas.height
  );
  this._data = this._image.data;
  if (parentTexture) {
    this._parentTexture.push(parentTexture);
    parentTexture.image = this._canvas;
  }
}

CanvasTexture.prototype = {
  constructor: CanvasTexture,
  _xCross: -1,
  _yCross: -1,
  _parentTexture: [],

  addParent: function (parentTexture) {
    if (this._parentTexture.indexOf(parentTexture) === -1) {
      this._parentTexture.push(parentTexture);
      parentTexture.image = this._canvas;
    }
  },

  drawPixel: function (x, y) {
    this._xCross = Math.round(x * this._canvas.width - 0.5);
    this._yCross = Math.round(y * this._canvas.height - 0.5);
    this._draw();
  },

  erasePixel: function (_x, _y, overlay) {
    const context = this._context2D;
    const x = Math.round(_x * this._canvas.width - 0.5);
    const y = Math.round(_y * this._canvas.height - 0.5);
    context.fillStyle = "#ffffff";

    const size = model === "steve" ? 4 : 3;

    if (overlay) {
      context.clearRect(x, y, 1, 1);

      if (isMirrored) {
        const mirrorMap = convertedMaps.find(
          (item) => item.intersected(x, y) && (!item.size || item.size === size)
        );

        if (mirrorMap) {
          const reflection = mirrorMap.findReflection(x, y);
          context.clearRect(reflection.x, reflection.y, 1, 1);
        }
      }
    } else {
      context.beginPath();
      context.rect(x, y, 1, 1);
      context.fill();

      if (isMirrored) {
        const mirrorMap = convertedMaps.find(
          (item) => item.intersected(x, y) && (!item.size || item.size === size)
        );

        if (mirrorMap) {
          const reflection = mirrorMap.findReflection(x, y);
          context.rect(reflection.x, reflection.y, 1, 1);
          context.fill();
        }
      }
    }

    this._updateTextures();
  },

  fillPixels: function (x, y, w, h) {
    const _w = Math.round(w * this._canvas.width - 0.5);
    const _h = Math.round(h * this._canvas.height - 0.5);
    const _x = Math.round(x * this._canvas.width - 0.5);
    const _y = Math.round(y * this._canvas.height - 0.5);

    const context = this._context2D;
    context.fillStyle = color || "#000000";
    context.translate(0, 64);
    context.scale(1, -1);

    context.beginPath();
    context.rect(_x, _y, _w, _h);
    context.fill();

    context.translate(0, 64);
    context.scale(1, -1);

    this._updateTextures();
    onSaveHistory();
  },

  colorPick: function (x, y) {
    const _x = Math.round(x * this._canvas.width - 0.5);
    const _y = Math.round(y * this._canvas.height - 0.5);
    return this._context2D.getImageData(_x, _y, 1, 1).data;
  },

  updateTextures: function () {
    this._updateTextures();
  },

  _updateTextures: function () {
    for (let i = 0; i < this._parentTexture.length; i++) {
      this._parentTexture[i].needsUpdate = true;
    }
  },

  _draw: function () {
    const context = this._context2D;

    if (!context) return;

    const x = this._xCross;
    const y = this._yCross;

    // Background.
    context.drawImage(drawingCanvas, 0, 0);

    // Fill pixel.

    context.fillStyle = color || "#000000";

    if (penMode === "autotone") {
      const max = 10;
      const min = -10;
      const random = Math.floor(Math.random() * (max - min + 1) + min);
      const toneColor =
        random < 0
          ? new TinyColor(color || "#000000").darken(random).toHexString()
          : new TinyColor(color || "#000000").lighten(random).toHexString();
      context.fillStyle = toneColor;
    }

    context.beginPath();
    context.rect(x, y, 1, 1);
    context.fill();

    const size = model === "steve" ? 4 : 3;

    if (isMirrored) {
      const mirrorMap = convertedMaps.find(
        (item) => item.intersected(x, y) && (!item.size || item.size === size)
      );

      if (mirrorMap) {
        const reflection = mirrorMap.findReflection(x, y);
        context.rect(reflection.x, reflection.y, 1, 1);
        context.fill();
      }
    }

    this._updateTextures();
  },
};

function setupCanvasDrawing() {
  // get canvas and context
  drawingCanvas = document.getElementById("drawing-canvas");
  drawingContext = drawingCanvas.getContext("2d");

  // hide canvas
  drawingCanvas.style.display = "none";

  skinTexture = new THREE.CanvasTexture(drawingCanvas);
  skinTexture.magFilter = THREE.NearestFilter;

  canvas = new CanvasTexture(skinTexture);

  // set the variable to keep track of when to draw
  let paint = false;
  drawingCanvas.addEventListener("pointerdown", function (e) {
    if (e.button === 0) {
      paint = true;
    }
    if (paint) canvas.drawPixel(e.offsetX / 4 / 64, e.offsetY / 4 / 64);
  });
  drawingCanvas.addEventListener("pointermove", function (e) {
    if (paint) canvas.drawPixel(e.offsetX / 4 / 64, e.offsetY / 4 / 64);
  });
  window.addEventListener("pointerup", function () {
    paint = false;
  });
}

//#region skin init
function initModel() {
  const rightLeg = createLeg(0, 0, false);
  rightLeg.position.set(-2, -10, 0);
  rightLeg.name = "RightLeg";

  const leftLeg = createLeg(0.25, -0.5, false);
  leftLeg.position.set(2, -10, 0);
  leftLeg.name = "LeftLeg";

  const rightLegO = createLeg(0, -0.25, true);
  rightLegO.position.set(-1.975, -10, 0);
  rightLegO.name = "RightLeg";

  const leftLegO = createLeg(0, -0.5, true);
  leftLegO.position.set(1.975, -10, 0);
  leftLegO.name = "LeftLeg";

  const rightArm = createArm(0.625, 0, false, false);
  rightArm.position.set(-6, 2, 0);
  rightArm.name = "RightArm";

  const leftArm = createArm(0.5, -0.5, false, false);
  leftArm.position.set(6, 2, 0);
  leftArm.name = "LeftArm";

  const rightArmO = createArm(0.625, -0.25, true, false);
  rightArmO.position.set(-5.975, 2, 0);
  rightArmO.name = "RightArm";

  const leftArmO = createArm(0.75, -0.5, true, false);
  leftArmO.position.set(5.975, 2, 0);
  leftArmO.name = "LeftArm";

  const rightArmSlim = createArm(0.625, 0, false, true);
  rightArmSlim.position.set(-5.5, 2, 0);
  rightArmSlim.name = "RightArm";
  rightArmSlim.visible = false;

  const leftArmSlim = createArm(0.5, -0.5, false, true);
  leftArmSlim.position.set(5.5, 2, 0);
  leftArmSlim.name = "LeftArm";
  leftArmSlim.visible = false;

  const rightArmSlimO = createArm(0.625, -0.25, true, true);
  rightArmSlimO.position.set(-5.475, 2, 0);
  rightArmSlimO.name = "RightArm";
  rightArmSlimO.visible = false;

  const leftArmSlimO = createArm(0.75, -0.5, true, true);
  leftArmSlimO.position.set(5.475, 2, 0);
  leftArmSlimO.name = "LeftArm";
  leftArmSlimO.visible = false;

  const head = createHead(0, 0, false);
  head.position.set(0, 12, 0);
  head.name = "Head";

  const headO = createHead(0.5, 0, true);
  headO.position.set(0, 12, 0);
  headO.name = "Head";

  const body = createBody(0, 0, false);
  body.position.set(0, 2, 0);
  body.name = "Body";

  const bodyO = createBody(0, -0.25, true);
  bodyO.position.set(0, 2, 0);
  bodyO.name = "Body";

  bodyGroup.add(
    rightArm,
    leftArm,
    body,
    head,
    rightLeg,
    leftLeg,
    rightArmSlim,
    leftArmSlim
  );
  overlayGroup.add(
    rightArmO,
    leftArmO,
    bodyO,
    headO,
    rightLegO,
    leftLegO,
    rightArmSlimO,
    leftArmSlimO
  );
  overlayGroup.children.forEach((item) => (item.isOverlay = true));
  bodyWireframeGroup = [
    rightArm.grid,
    leftArm.grid,
    body.grid,
    head.grid,
    rightLeg.grid,
    leftLeg.grid,
    rightArmSlim.grid,
    leftArmSlim.grid,
  ];
  bodyWireframeGroup.map((item) => (item.visible = !showOverlay));
  scene.add(bodyGroup, overlayGroup);

  alex = [rightArmSlim, leftArmSlim, rightArmSlimO, leftArmSlimO];
  steve = [rightArm, leftArm, rightArmO, leftArmO];
  alex.map((item) => (item.modelName = "alex"));
  steve.map((item) => (item.modelName = "steve"));
}

function createGrid(object, color) {
  const { width, height, depth } = object.geometry.parameters;
  const group = new THREE.Group();
  group.visible = true;

  const gridTop = new WireframedBox(width, depth, color);
  gridTop.position.set(0, height / 2 + 0.005, 0);
  group.add(gridTop);

  const gridBottom = new WireframedBox(width, depth, color);
  gridBottom.position.set(0, -(height / 2 + 0.005), 0);
  group.add(gridBottom);

  const gridFront = new WireframedBox(width, height, color);
  gridFront.position.set(0, 0, depth / 2 + 0.005);
  gridFront.rotation.x = Math.PI / 2;
  group.add(gridFront);

  const gridBack = new WireframedBox(width, height, color);
  gridBack.position.set(0, 0, -(depth / 2 + 0.005));
  gridBack.rotation.x = Math.PI / 2;
  group.add(gridBack);

  const gridLeft = new WireframedBox(height, depth, color);
  gridLeft.position.set(-(width / 2 + 0.005), 0, 0);
  gridLeft.rotation.z = Math.PI / 2;
  group.add(gridLeft);

  const gridRight = new WireframedBox(height, depth, color);
  gridRight.position.set(width / 2 + 0.005, 0, 0);
  gridRight.rotation.z = Math.PI / 2;
  group.add(gridRight);

  object.grid = group;
  object.add(group);
}

function createLeg(dx, dy, overlay) {
  var legSides = [];
  for (i = 0; i < 6; i++) {
    legSides.push(skinTexture.clone());
    canvas.addParent(legSides[i]);
  }

  legSides[0].repeat.set(0.0625, 0.1875);
  legSides[0].offset.set(0.125 + dx, 0.5 + dy);
  legSides[1].repeat.set(0.0625, 0.1875);
  legSides[1].offset.set(0.0 + dx, 0.5 + dy);
  legSides[2].repeat.set(0.0625, 0.0625);
  legSides[2].offset.set(0.0625 + dx, 0.6875 + dy);
  legSides[3].repeat.set(0.0625, 0.0625);
  legSides[3].offset.set(0.125 + dx, 0.6875 + dy);
  legSides[4].repeat.set(0.0625, 0.1875);
  legSides[4].offset.set(0.0625 + dx, 0.5 + dy);
  legSides[5].repeat.set(0.0625, 0.1875);
  legSides[5].offset.set(0.1875 + dx, 0.5 + dy);
  var legMaterials = [];
  for (i = 0; i < 6; i++) {
    legMaterials.push(
      new THREE.MeshBasicMaterial({
        map: legSides[i],
        transparent: overlay,
        side: THREE.DoubleSide,
      })
    );
  }
  const object = new THREE.Mesh(new THREE.BoxGeometry(4, 12, 4), legMaterials);
  createGrid(object, 0x666666);

  if (overlay) {
    object.scale.x = 4.45 / 4;
    object.scale.y = 12.45 / 12;
    object.scale.z = 4.45 / 4;
  }

  return object;
}

function createArm(dx, dy, overlay, slim) {
  var armSides = [];
  for (i = 0; i < 6; i++) {
    armSides.push(skinTexture.clone());
    canvas.addParent(armSides[i]);
  }

  // Right
  armSides[0].repeat.set(0.0625, 0.1875);
  armSides[0].offset.set(0.125 - (slim ? 0.015625 : 0) + dx, 0.5 + dy);

  // Left
  armSides[1].repeat.set(0.0625, 0.1875);
  armSides[1].offset.set(0.0 + dx, 0.5 + dy);

  // Top
  armSides[2].repeat.set(slim ? 0.046875 : 0.0625, 0.0625);
  armSides[2].offset.set(0.0625 + dx, 0.6875 + dy);

  // Bottom
  armSides[3].repeat.set(slim ? 0.046875 : 0.0625, 0.0625);
  armSides[3].offset.set(0.125 - (slim ? 0.015625 : 0) + dx, 0.6875 + dy);

  // Front
  armSides[4].repeat.set(slim ? 0.046875 : 0.0625, 0.1875);
  armSides[4].offset.set(0.0625 + dx, 0.5 + dy);

  // Back
  armSides[5].repeat.set(slim ? 0.046875 : 0.0625, 0.1875);
  armSides[5].offset.set(0.1875 - (slim ? 0.015625 : 0) + dx, 0.5 + dy);

  var armMaterials = [];
  for (i = 0; i < 6; i++) {
    armMaterials.push(
      new THREE.MeshBasicMaterial({
        map: armSides[i],
        transparent: overlay,
        side: THREE.DoubleSide,
      })
    );
  }
  const armWidth = slim ? 3 : 4;
  const object = new THREE.Mesh(
    new THREE.BoxGeometry(armWidth, 12, 4),
    armMaterials
  );
  createGrid(object, 0x666666);

  if (overlay) {
    object.scale.x = (armWidth + 0.45) / armWidth;
    object.scale.y = 12.45 / 12;
    object.scale.z = 4.45 / 4;
  }

  return object;
}

function createHead(dx, dy, overlay) {
  var headSides = [];

  for (i = 0; i < 6; i++) {
    headSides.push(skinTexture.clone());
    canvas.addParent(headSides[i]);
  }

  headSides[0].offset.set(0.25 + dx, 0.75);
  headSides[1].offset.set(0 + dx, 0.75);
  headSides[2].offset.set(0.125 + dx, 0.875);
  headSides[3].offset.set(0.375 + dx, 1);
  headSides[3].rotation = Math.PI;
  headSides[4].offset.set(0.125 + dx, 0.75);
  headSides[5].offset.set(0.375 + dx, 0.75);
  var headMaterials = [];
  for (i = 0; i < 6; i++) {
    headSides[i].repeat.set(0.125, 0.125);
    headMaterials.push(
      new THREE.MeshBasicMaterial({
        map: headSides[i],
        transparent: overlay,
        side: THREE.DoubleSide,
      })
    );
  }
  const object = new THREE.Mesh(new THREE.BoxGeometry(8, 8, 8), headMaterials);
  createGrid(object, 0x666666);

  if (overlay) {
    object.scale.x = 8.45 / 8;
    object.scale.y = 8.45 / 8;
    object.scale.z = 8.45 / 8;
  }

  return object;
}

function createBody(dx, dy, overlay) {
  var bodySides = [];

  for (i = 0; i < 6; i++) {
    bodySides.push(skinTexture.clone());
    canvas.addParent(bodySides[i]);
  }

  bodySides[0].repeat.set(0.0625, 0.1875);
  bodySides[0].offset.set(0.4375, 0.5 + dy);
  bodySides[1].repeat.set(0.0625, 0.1875);
  bodySides[1].offset.set(0.25, 0.5 + dy);
  bodySides[2].repeat.set(0.125, 0.0625);
  bodySides[2].offset.set(0.3125, 0.6875 + dy);
  bodySides[3].repeat.set(0.125, 0.0625);
  bodySides[3].offset.set(0.4375, 0.6875 + dy);
  bodySides[4].repeat.set(0.125, 0.1875);
  bodySides[4].offset.set(0.3125, 0.5 + dy);
  bodySides[5].repeat.set(0.125, 0.1875);
  bodySides[5].offset.set(0.5, 0.5 + dy);
  var bodyMaterials = [];
  for (i = 0; i < 6; i++) {
    bodyMaterials.push(
      new THREE.MeshBasicMaterial({
        map: bodySides[i],
        transparent: overlay,
        side: THREE.DoubleSide,
      })
    );
  }
  const object = new THREE.Mesh(new THREE.BoxGeometry(8, 12, 4), bodyMaterials);
  createGrid(object, 0x666666);

  if (overlay) {
    object.scale.x = 8.45 / 8;
    object.scale.y = 12.45 / 12;
    object.scale.z = 4.45 / 4;
  }

  return object;
}
//#endregion

function resizeCanvasToDisplaySize() {
  const canvas = renderer.domElement;
  const width = canvas.clientWidth;
  const height = canvas.clientHeight;
  if (canvas.width !== width || canvas.height !== height) {
    // you must pass false here or three.js sadly fights the browser
    renderer.setSize(width, height, false);
    camera.aspect = width / height;
    camera.updateProjectionMatrix();

    // set render target sizes here
  }
}

function animate() {
  resizeCanvasToDisplaySize();
  requestAnimationFrame(animate);
  controls.update();
  renderer.render(scene, camera);
}

function updateSkinLayer() {
  drawingContext.clearRect(0, 0, drawingCanvas.width, drawingCanvas.height);
  if (layerCanvas) drawingContext.drawImage(layerCanvas, 0, 0);
  canvas.updateTextures();
}

function initSkinLayer(texture, forced = false) {
  if (layerCanvas && !forced) return;

  var layer = document.createElement("canvas");
  var layerContext = layer.getContext("2d");
  layerContext.imageSmoothingEnabled = false;

  var image = new THREE.ImageLoader().load(texture.src, function () {
    layerContext.drawImage(image, 0, 0);
    updateSkinLayer();
  });

  layerCanvas = layer;
}

class Scene extends React.Component {
  hiddenParts = [];

  componentDidMount() {
    init();
    setupCanvasDrawing();
    initModel();
    animate();
  }

  render() {
    penMode = this.props.penMode;
    onColorPick = this.props.onColorPick;
    onSaveHistory = this.props.onSaveHistory;
    isMirrored = this.props.isMirrored;
    initSkinLayer(this.props.texture);

    return (
      <div style={{ padding: "0 50px" }}>
        <div id="container" className="flex-fill">
          <canvas
            id="renderer-canvas"
            className="renderer-canvas"
            height="200" ></canvas>
          <canvas
            id="drawing-canvas"
            className="drawing-canvas "
            height="64"
            width="64" ></canvas>
          <canvas
            id="import-canvas"
            height="64"
            width="64"
            className="d-none" ></canvas>
        </div>
      </div>
    );
  }

  setModel(_model) {
    model = _model;
    alex.map((item) => (item.visible = model === "alex"));
    steve.map((item) => (item.visible = model === "steve"));
    const bodyParts = bodyGroup.children.filter(
      (item) => !item.modelName || item.modelName === model
    );

    bodyParts.map(
      (item) =>
        (item.visible = showBody && !this.hiddenParts.includes(item.name))
    );

    const overlayParts = overlayGroup.children.filter(
      (item) => !item.modelName || item.modelName === model
    );
    overlayParts.map(
      (item) =>
        (item.visible = showOverlay && !this.hiddenParts.includes(item.name))
    );

    bodyWireframeGroup.map((item) => (item.visible = !showOverlay));
  }

  toggleParts(hidden, _showBody, _showOverlay) {
    this.hiddenParts = hidden;
    showBody = _showBody;
    showOverlay = _showOverlay;
    this.setModel(model);
  }

  setColor(_color) {
    color = _color;
  }

  setTexture(texture) {
    initSkinLayer(texture, true);
  }

  zoomIn() {
    camera.position.x /= 1.1;
    camera.position.y /= 1.1;
    camera.position.z /= 1.1;
  }

  zoomOut() {
    camera.position.x *= 1.1;
    camera.position.y *= 1.1;
    camera.position.z *= 1.1;
  }
}

export default Scene;
